// Tokens: standard Jinja {{ expr }} first, legacy single-brace {expr} second. Emits Jinja2 either way.
// The shared component still rejects literal {{ }} in variable fields until its placeholder check is numeric-only.
const TOKEN_RE = /\{\{\s*([^{}]+?)\s*\}\}|\{([^{}]+)\}/g;

export function tokenToExpression(tokenInner) {
  // valueToJinja always appends "| json"; drop a hand-written one so it isn't encoded twice.
  const trimmed = (tokenInner || '').trim().replace(/\s*\|\s*json\s*$/i, '');
  if (trimmed === 'sent_timestamp') return 'time';

  // A leading customer./event. path becomes bracket form (Jinja subscript), not dot-attribute
  // access — dot access tries getattr() first, so "customer.items" resolves dict.items (the
  // built-in method) instead of the "items" key, and a chained path like "customer.address.city"
  // raises UndefinedError as soon as "address" is missing instead of degrading like a subscript
  // miss does. Only the leading identifier chain is rewritten; any filter/operator after it (e.g.
  // "| upper") is left untouched. "customer.items | upper" -> "customer['items'] | upper";
  // "customer.address.city" -> "customer['address']['city']".
  let match = trimmed.match(/^(customer|event)((?:\.[A-Za-z0-9_]+)+)(.*)$/s);
  if (match) {
    const [, root, path, rest] = match;
    const bracketed = path
      .slice(1)
      .split('.')
      .map((segment) => `['${segment}']`)
      .join('');
    return `${root}${bracketed}${rest}`;
  }

  match = trimmed.match(/^(scenario|action)\.(id|name)$/);
  if (match) return `${match[1]}['${match[2]}']`;

  // Fallback: a raw Jinja expression the user typed directly, e.g. {customer['x'] | upper}.
  return trimmed;
}

export function jinjaStringLiteral(value) {
  const escaped = String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `"${escaped}"`;
}

// Splits a value into alternating literal/token segments using TOKEN_RE.
function splitSegments(value) {
  const segments = [];
  let lastIndex = 0;
  let match;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(value)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'literal', text: value.slice(lastIndex, match.index) });
    }
    const inner = match[1] ?? match[2];
    // {% ... %} / {# ... #} are Jinja statement/comment syntax, not a token — TOKEN_RE's
    // single-brace alternative still matches them (inner text like "% if vip %"). A marketer
    // typing that literally would otherwise fall through tokenToExpression raw and land inside
    // our {{ ... }} expression, breaking the whole render with a TemplateSyntaxError. Treat it as
    // literal text instead, braces and all.
    if (/^[%#]/.test(inner.trim())) {
      segments.push({ type: 'literal', text: match[0] });
    } else {
      segments.push({ type: 'token', text: inner });
    }
    lastIndex = TOKEN_RE.lastIndex;
  }
  if (lastIndex < value.length) {
    segments.push({ type: 'literal', text: value.slice(lastIndex) });
  }
  return segments;
}

// Returns {raw:true} for a Jinja expression, {raw:false, text:JSON string} otherwise.
export function valueToJinja(value) {
  const str = String(value ?? '');
  const segments = splitSegments(str);
  const tokenCount = segments.filter((segment) => segment.type === 'token').length;

  if (tokenCount === 0) {
    // A literal {%, {# or {{ sitting in plain JSON.stringify'd text is still live Jinja syntax to
    // Bloomreach's renderer -- it isn't inside any {{ ... }} of ours, so nothing protects it.
    // Emit the whole value as a quoted Jinja string literal instead: Jinja's lexer treats
    // everything between the quotes as inert string content, never re-entering template-markup
    // parsing for braces it finds there.
    if (/\{%|\{#|\{\{/.test(str)) {
      return { raw: true, text: `{{ ${jinjaStringLiteral(str)} | json }}` };
    }
    return { raw: false, text: JSON.stringify(value) };
  }

  if (segments.length === 1 && segments[0].type === 'token') {
    // "| string" first: a bare token can be a number/boolean (goes out as a JSON number/boolean
    // instead of a string — buttonUrlParam/ctaButtonUrl then fail gs-intg's DTO with "must be a
    // string") or a missing attribute (default Undefined isn't JSON-serializable on its own).
    // Matches the destination field's own "| string | json" above.
    return { raw: true, text: `{{ ${tokenToExpression(segments[0].text)} | string | json }}` };
  }

  const pieces = segments
    .filter((segment) => segment.text !== '')
    .map((segment) => (segment.type === 'token' ? tokenToExpression(segment.text) : jinjaStringLiteral(segment.text)));
  return { raw: true, text: `{{ (${pieces.join(' ~ ')}) | json }}` };
}
